import type { CaptureProcessingProgress, FolderAnalysisProgress } from '../shared/desktop-api';
import type { EngineCall } from './desktop-commands';
import { isTransient } from './engine-unreachable';
import { whileHeld } from './page-holds';

/**
 * A `process` is a run, not a call (services/Paige.Engine.Host/ProcessRuns.cs). The desktop's main
 * process held its engine on a pipe and progress lines streamed back while a folder was read; over
 * HTTP the one call took as long as the reading and nothing came back until it ended — the screen
 * said "Opening the first page" for twenty-four minutes on the first customer run (break 16), and
 * the API's channel gave up at thirty. So the host answers a ticket at once and `process-status`
 * is asked until the ticket says the run is over; each ask carries where the reading has got to,
 * which is the progress the screen's own subscriptions are fed (`onFolderAnalysisProgress`,
 * `onCaptureProcessingProgress`). An engine that answers a `process` outright is taken as it is.
 */
export interface RunTicket {
  runId: string;
  jobId: string;
  running: true;
  startedAt: string;
  progress: RawProgress | null;
  /** Every event since the sequence this ask named (`offset`), in order (ProgressJournal.cs). */
  events?: RawProgress[] | null;
  /** The sequence to name on the next ask. */
  sequence?: number;
}

/** The engine's own progress record: `documentTypes` is null until a type is named, and the job is not on it. */
export type RawProgress = Omit<
  FolderAnalysisProgress,
  'jobId' | 'documentTypes' | 'completedPageCount'
> & {
  documentTypes?: string[] | null;
  completedPageCount?: number | null;
};

/** How often a run is asked after: a few frames a minute is plenty for a reading measured in minutes. */
export const pollEvery = 1500;
/**
 * Failed asks in a row before the run is given up on: a minute of them, long enough for an API pod
 * to drain and be replaced. An engine's own refusal ends the run at once.
 */
const failuresAllowed = 40;

export function isTicket(answer: unknown): answer is RunTicket {
  return (
    typeof answer === 'object' &&
    answer !== null &&
    (answer as RunTicket).running === true &&
    typeof (answer as RunTicket).runId === 'string'
  );
}

/**
 * The engine's `process`, followed to its reading; `onProgress` sees every ask that had news. The
 * page is held from the ask to the answer (page-holds.ts): the run survives a reload on the host,
 * but the screen following it would not, so Paige never reloads itself onto a newer version here.
 */
export function runProcess<T>(
  call: EngineCall,
  args: Record<string, unknown>,
  onProgress: (progress: RawProgress, ticket: RunTicket) => void,
  wait: (ms: number) => Promise<void> = sleep,
): Promise<T> {
  return whileHeld('run', async () => {
    const first = await call<T | RunTicket>('process', args);
    return isTicket(first) ? followRun<T>(call, first, onProgress, wait) : first;
  });
}

/**
 * A run already under way (its ticket from `process`, or from `process-status` after a reload),
 * asked after until it answers the reading (`status` names the question: `reprocess-status` for a
 * batch read again, which the API does not mirror into the ledger as a new reading). A refusal from the engine — the run failed, or nobody
 * holds it any more — is thrown as it came; a request that did not reach the engine is tried again
 * a few times, because a reading that costs minutes should not be lost to one dropped connection.
 * The page is held while the run is followed (page-holds.ts).
 */
export function followRun<T>(
  call: EngineCall,
  ticket: RunTicket,
  onProgress: (progress: RawProgress, ticket: RunTicket) => void,
  wait: (ms: number) => Promise<void> = sleep,
  status = 'process-status',
): Promise<T> {
  return whileHeld('run', () => follow<T>(call, ticket, onProgress, wait, status));
}

async function follow<T>(
  call: EngineCall,
  ticket: RunTicket,
  onProgress: (progress: RawProgress, ticket: RunTicket) => void,
  wait: (ms: number) => Promise<void>,
  status: string,
): Promise<T> {
  if (ticket.progress) onProgress(ticket.progress, ticket);
  let failures = 0;
  let seen = 0;
  for (;;) {
    await wait(pollEvery);
    let answer: T | RunTicket;
    try {
      answer = await call<T | RunTicket>(status, {
        jobId: ticket.jobId,
        runId: ticket.runId,
        offset: seen,
      });
      failures = 0;
    } catch (reason) {
      if (!isTransient(reason) || ++failures >= failuresAllowed) throw reason;
      continue;
    }
    if (!isTicket(answer)) return answer;
    seen = handOn(answer, seen, onProgress);
  }
}

/**
 * Each event the run recorded since the last ask, in order, as the desktop's pipe handed every one
 * to its screen; with nothing new, the latest again as a heartbeat (the reading is alive). A host
 * that keeps no events answers the latest alone. Answers the sequence to ask after next.
 */
function handOn(
  answer: RunTicket,
  seen: number,
  onProgress: (progress: RawProgress, ticket: RunTicket) => void,
): number {
  if (!answer.events) {
    if (answer.progress) onProgress(answer.progress, answer);
    return seen;
  }
  for (const event of answer.events) onProgress(event, answer);
  if (answer.events.length === 0 && answer.progress)
    onProgress({ ...answer.progress, heartbeat: true }, answer);
  return answer.sequence ?? seen;
}

/** The screen's shape: the job on it, the arrays the renderer reads without checking. */
export function progressFor(raw: RawProgress, jobId: string): FolderAnalysisProgress {
  return {
    ...raw,
    jobId,
    documentTypes: raw.documentTypes ?? [],
    completedPageCount: raw.completedPageCount ?? 0,
  };
}

/** What the desktop's IPC channel was: subscribers on this side of the seam, one event to each. */
export interface ProgressBus<T> {
  subscribe: (callback: (progress: T) => void) => () => void;
  publish: (progress: T) => void;
}

export function progressBus<T>(): ProgressBus<T> {
  const subscribers = new Set<(progress: T) => void>();
  return {
    subscribe: (callback) => {
      subscribers.add(callback);
      return () => {
        subscribers.delete(callback);
      };
    },
    publish: (progress) => {
      for (const callback of subscribers) callback(progress);
    },
  };
}

/** The setup sample's progress, for the App's `onFolderAnalysisProgress`. */
export const folderProgress = progressBus<FolderAnalysisProgress>();
/** A staged capture's progress, for the App's `onCaptureProcessingProgress`. */
export const captureProgress = progressBus<CaptureProcessingProgress>();

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
